import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { constructValue } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { of, switchMap } from 'rxjs';
import { SmartTeamsAvailability } from '../db/SmartTeamsAvailability.js';
import { SmartTeamsPartnerCompany } from '../db/SmartTeamsCompany.js';
import { SmartTeamsCustomer } from '@nsc/smartteams-shared/db/SmartTeamsCustomer.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { getPartnerCompanyFilter } from '../features/permissions/api/getCompanyFilter.js';
import { getFieldEngineerFilter } from '../features/permissions/api/getFieldEngineerFilter.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

/**
 * Get allowed partner companies for current user.
 */
export const getAllowedPartnerCompanies = registerQuery({
  operationName: 'getAllowedPartnerCompanies',
  type: () => [SmartTeamsPartnerCompany],
  params: [],
  resolve:
    () =>
    async (_gqlFields: BaseQLSelectionSet<SmartTeamsPartnerCompany>): Promise<SmartTeamsPartnerCompany[]> => {
      const companyFilter = await getPartnerCompanyFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 });
      const collection = await getRawCollection<SmartTeamsPartnerCompany>(SmartTeamsPartnerCompany._collectionName);
      return collection
        .find<SmartTeamsPartnerCompany>({
          archived: { $ne: true },
          deletedAt: null,
          ...companyFilter.parseMongoFilter(),
        })
        .sort({ name: 1 })
        .toArray();
    },
});

// Customers — gated on CustomerRead. No row-level scoping yet (single tenant per install).
export const subscribeCustomersPaginated = registerSubscription({
  operationName: 'subscribeCustomersPaginated',
  type: () => [SmartTeamsCustomer],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve:
    (skip: number, limit: number, filter?: AdornisFilter) => (_gqlFields: BaseQLSelectionSet<SmartTeamsCustomer>) => {
      const userFilter = filter?.getMongoFilter() ?? {};
      const combinedFilter = Object.keys(userFilter).length
        ? { $and: [{ deletedAt: null }, userFilter] }
        : { deletedAt: null };
      return of(null).pipe(
        switchMap(async () => {
          const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.CustomerRead));
          if (!allowed) throw new Error('Insufficient permissions to view customers');
          return watchQuery<SmartTeamsCustomer>(SmartTeamsCustomer._collectionName, combinedFilter);
        }),
        switchMap(obs => obs),
        switchMap(async () => {
          const col = await getRawCollection<SmartTeamsCustomer>(SmartTeamsCustomer._collectionName);
          return col.find<SmartTeamsCustomer>(combinedFilter).skip(skip).limit(limit).sort({ name: 1 }).toArray();
        }),
      );
    },
});

// Users for the User Management admin list. Gated on UserManage; hides soft-deleted users
// (`deletedAt == null` matches null and missing). Mirrors the customer list shape so search/filter
// keep working via the AdornisFilter param.
export const subscribeUsersPaginated = registerSubscription({
  operationName: 'subscribeUsersPaginated',
  type: () => [AdornisUser],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve: (skip: number, limit: number, filter?: AdornisFilter) => (_gqlFields: BaseQLSelectionSet<AdornisUser>) => {
    const userFilter = filter?.getMongoFilter() ?? {};
    const combinedFilter = Object.keys(userFilter).length
      ? { $and: [{ deletedAt: null }, userFilter] }
      : { deletedAt: null };
    return of(null).pipe(
      switchMap(async () => {
        const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
        if (!allowed) throw new Error('Insufficient permissions to view users');
        return watchQuery<AdornisUser>(AdornisUser._collectionName, combinedFilter);
      }),
      switchMap(obs => obs),
      switchMap(async () => {
        const col = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
        return col.find<AdornisUser>(combinedFilter).skip(skip).limit(limit).sort({ email: 1 }).toArray();
      }),
    );
  },
});

/**
 * Subscribe to allowed partner companies (live updates).
 */
export const subscribeAllowedPartnerCompanies = registerSubscription({
  operationName: 'subscribeAllowedPartnerCompanies',
  type: () => [SmartTeamsPartnerCompany],
  params: [],
  resolve: () => (_gqlFields: BaseQLSelectionSet<SmartTeamsPartnerCompany>) => {
    return of(
      getPartnerCompanyFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(filter =>
        filter.parseMongoFilter(),
      ),
    ).pipe(
      switchMap(filter =>
        watchQuery<SmartTeamsPartnerCompany>(SmartTeamsPartnerCompany._collectionName, {
          archived: { $ne: true },
          deletedAt: null,
          ...filter,
        }),
      ),
    );
  },
});

/**
 * Get scoped field engineers (optionally filtered by companyId).
 */
export const getScopedFieldEngineers = registerQuery({
  operationName: 'getScopedFieldEngineers',
  type: () => [SmartTeamsFieldEngineer],
  params: [{ name: 'companyId', type: () => String }],
  resolve:
    (companyId?: string) =>
    async (_gqlFields: BaseQLSelectionSet<SmartTeamsFieldEngineer>): Promise<SmartTeamsFieldEngineer[]> => {
      const filter = await getFieldEngineerFilter(companyId)({
        allowedContexts: 1,
        hasAccess: 1,
        isGlobal: 1,
        companyFilterId: 1,
      }).then(f => f.parseMongoFilter());
      const collection = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
      return collection.find<SmartTeamsFieldEngineer>({ ...filter, deletedAt: null }).sort({ name: 1 }).toArray();
    },
});

/**
 * Subscribe to scoped field engineers.
 */
export const subscribeScopedFieldEngineers = registerSubscription({
  operationName: 'subscribeScopedFieldEngineers',
  type: () => [SmartTeamsFieldEngineer],
  params: [{ name: 'companyId', type: () => String }],
  resolve: (companyId?: string) => (gqlFields: BaseQLSelectionSet<SmartTeamsFieldEngineer>) => {
    return watchEntireCollection(SmartTeamsFieldEngineer._collectionName).pipe(
      switchMap(_ => getScopedFieldEngineers(companyId)(gqlFields)),
    );
  },
});

/**
 * Subscribe to scoped availabilities for a field engineer.
 */
export const subscribeScopedAvailabilities = registerSubscription({
  operationName: 'subscribeScopedAvailabilities',
  type: () => [SmartTeamsAvailability],
  params: [
    { name: 'fieldEngineerId', type: () => String },
    { name: 'startDate', type: () => Date },
    { name: 'endDate', type: () => Date },
  ],
  resolve:
    (fieldEngineerId?: string, startDate?: Date, endDate?: Date) =>
    (_gqlFields: BaseQLSelectionSet<SmartTeamsAvailability>) => {
      return watchEntireCollection(SmartTeamsAvailability._collectionName).pipe(
        switchMap(async () => {
          const fieldEngineerFilter = await getFieldEngineerFilter()({
            allowedContexts: 1,
            hasAccess: 1,
            isGlobal: 1,
          }).then(f => f.parseMongoFilter());
          const feCollection = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
          const avCollection = await getRawCollection<SmartTeamsAvailability>(SmartTeamsAvailability._collectionName);

          // Get allowed FE IDs
          const allowedFEs = await feCollection
            .find({ archived: { $ne: true }, deletedAt: null, ...fieldEngineerFilter })
            .toArray();
          const allowedFEIds = allowedFEs.map(fe => fe._id);

          if (allowedFEIds.length === 0) return [];

          // Build availability query
          const selector: Record<string, unknown> = { fieldEngineerId: { $in: allowedFEIds }, deletedAt: null };
          if (fieldEngineerId) {
            if (!allowedFEIds.includes(fieldEngineerId)) return [];
            selector.fieldEngineerId = fieldEngineerId;
          }
          if (startDate && endDate) {
            selector.date = { $gte: startDate, $lte: endDate };
          }

          const availabilities = await avCollection
            .find<SmartTeamsAvailability>(selector)
            .sort({ date: 1, startTime: 1 })
            .toArray();

          // Merge default locations
          const feMap = new Map(allowedFEs.map(fe => [fe._id, fe]));
          for (const av of availabilities) {
            if (av.useDefaultLocation && av.fieldEngineerId) {
              const fe = feMap.get(av.fieldEngineerId);
              if (fe?.defaultLocation) {
                av.location = fe.defaultLocation;
              }
            }
          }

          return availabilities;
        }),
      );
    },
});

/**
 * Get partner company by ID (with permission check).
 */
export const getPartnerCompanyById = registerQuery({
  operationName: 'getPartnerCompanyById',
  type: () => SmartTeamsPartnerCompany,
  params: [{ name: 'id', type: () => String }],
  resolve:
    (id: string) =>
    async (_gqlFields: BaseQLSelectionSet<SmartTeamsPartnerCompany>): Promise<SmartTeamsPartnerCompany | null> => {
      const filter = await getPartnerCompanyFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(f =>
        f.parseMongoFilter(),
      );
      const collection = await getRawCollection<SmartTeamsPartnerCompany>(SmartTeamsPartnerCompany._collectionName);
      return collection.findOne<SmartTeamsPartnerCompany>({ _id: id, ...filter });
    },
});

/**
 * Get field engineer by ID (with permission check).
 */
export const getFieldEngineerById = registerQuery({
  operationName: 'getFieldEngineerById',
  type: () => SmartTeamsFieldEngineer,
  params: [{ name: 'id', type: () => String }],
  resolve:
    (id: string) =>
    async (_gqlFields: BaseQLSelectionSet<SmartTeamsFieldEngineer>): Promise<SmartTeamsFieldEngineer | null> => {
      const filter = await getFieldEngineerFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(f =>
        f.parseMongoFilter(),
      );
      const collection = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
      return collection.findOne<SmartTeamsFieldEngineer>({ _id: id, ...filter });
    },
});

// ============================================================================
// Paginated subscriptions for Entity Manager (with permission scoping)
// ============================================================================

/**
 * Subscribe to scoped partner companies with pagination and filtering.
 * For use with GenericEntityManager's `operation` property.
 */
export const subscribeScopedPartnerCompaniesPaginated = registerSubscription({
  operationName: 'subscribeScopedPartnerCompaniesPaginated',
  type: () => [SmartTeamsPartnerCompany],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve:
    (skip: number, limit: number, filter?: AdornisFilter) =>
    (_gqlFields: BaseQLSelectionSet<SmartTeamsPartnerCompany>) => {
      return getPartnerCompanyFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 })
        .then(filter => filter.parseMongoFilter())
        .then(companyFilter => {
          const userFilter = filter?.getMongoFilter() ?? {};

          // Combine permission filter with user filter
          const conditions = [{ archived: { $ne: true } }, { deletedAt: null }, companyFilter, userFilter].filter(
            f => Object.keys(f).length > 0,
          );

          const combinedFilter = conditions.length > 1 ? { $and: conditions } : (conditions[0] ?? {});

          return watchQuery<SmartTeamsPartnerCompany>(SmartTeamsPartnerCompany._collectionName, combinedFilter);
        });
    },
});

/**
 * Subscribe to scoped field engineers with pagination and filtering.
 * For use with GenericEntityManager's `operation` property.
 */
export const subscribeScopedFieldEngineersPaginated = registerSubscription({
  operationName: 'subscribeScopedFieldEngineersPaginated',
  type: () => [SmartTeamsFieldEngineer],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve:
    (skip: number, limit: number, filter?: AdornisFilter) =>
    (_gqlFields: BaseQLSelectionSet<SmartTeamsFieldEngineer>) => {
      return getFieldEngineerFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 })
        .then(f => f.parseMongoFilter())
        .then(fieldEngineerFilter =>
          watchEntireCollection(SmartTeamsFieldEngineer._collectionName).pipe(
            switchMap(async () => {
              const coll = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(
                SmartTeamsFieldEngineer._collectionName,
              );
              return (
                await coll
                  .find({ $and: [{ deletedAt: null }, fieldEngineerFilter, filter?.getMongoFilter() ?? {}] })
                  .skip(skip)
                  .limit(limit)
                  .toArray()
              ).map(e => constructValue<SmartTeamsFieldEngineer>(e));
            }),
          ),
        );
    },
});

/**
 * Subscribe to scoped availabilities with pagination and filtering.
 * For use with GenericEntityManager's `operation` property.
 *
 * Note: Availabilities are scoped by field engineer's company.
 * PM without companies sees nothing.
 */
export const subscribeScopedAvailabilitiesPaginated = registerSubscription({
  operationName: 'subscribeScopedAvailabilitiesPaginated',
  type: () => [SmartTeamsAvailability],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve:
    (skip: number, limit: number, filter?: AdornisFilter) =>
    (_gqlFields: BaseQLSelectionSet<SmartTeamsAvailability>) => {
      // Watch the availabilities collection itself so creating/deleting an availability refreshes
      // the list. (Watching the field-engineer collection — as this used to — only re-emitted on FE
      // changes, so new/deleted availabilities required a page reload.)
      return watchEntireCollection(SmartTeamsAvailability._collectionName).pipe(
        switchMap(async () => {
          const fieldEngineerFilter = await getFieldEngineerFilter()({
            allowedContexts: 1,
            hasAccess: 1,
            isGlobal: 1,
          }).then(f => f.parseMongoFilter());
          const feCollection = await getRawCollection<SmartTeamsFieldEngineer>(
            SmartTeamsFieldEngineer._collectionName,
          );
          const fieldEngineers = await feCollection.find({ ...fieldEngineerFilter, deletedAt: null }).toArray();
          const availabilityCollection = await getRawCollection<SmartTeamsAvailability>(
            SmartTeamsAvailability._collectionName,
          );
          return availabilityCollection
            .find<SmartTeamsAvailability>({
              fieldEngineerId: { $in: fieldEngineers.map(engineer => engineer._id) },
              deletedAt: null,
            })
            .skip(skip)
            .limit(limit)
            .toArray();
        }),
      );
    },
});

// ============================================================================
// Record counts for list views (total matching the current filter, after scoping)
// Each mirrors the mongo filter of its sibling paginated subscription above so the number the list
// header shows equals the number of rows the list would page through.
// ============================================================================

export const countScopedFieldEngineers = registerQuery({
  operationName: 'countScopedFieldEngineers',
  type: () => Int,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: (filter?: AdornisFilter) => async (): Promise<number> => {
    const fieldEngineerFilter = await getFieldEngineerFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(
      f => f.parseMongoFilter(),
    );
    const coll = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
    return coll.countDocuments({ $and: [{ deletedAt: null }, fieldEngineerFilter, filter?.getMongoFilter() ?? {}] });
  },
});

export const countScopedPartnerCompanies = registerQuery({
  operationName: 'countScopedPartnerCompanies',
  type: () => Int,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: (filter?: AdornisFilter) => async (): Promise<number> => {
    const companyFilter = await getPartnerCompanyFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(f =>
      f.parseMongoFilter(),
    );
    const userFilter = filter?.getMongoFilter() ?? {};
    const conditions = [{ archived: { $ne: true } }, { deletedAt: null }, companyFilter, userFilter].filter(
      f => Object.keys(f).length > 0,
    );
    const combinedFilter = conditions.length > 1 ? { $and: conditions } : (conditions[0] ?? {});
    const coll = await getRawCollection<SmartTeamsPartnerCompany>(SmartTeamsPartnerCompany._collectionName);
    return coll.countDocuments(combinedFilter);
  },
});

export const countCustomers = registerQuery({
  operationName: 'countCustomers',
  type: () => Int,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: (filter?: AdornisFilter) => async (): Promise<number> => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.CustomerRead));
    if (!allowed) throw new Error('Insufficient permissions to view customers');
    const userFilter = filter?.getMongoFilter() ?? {};
    const combinedFilter = Object.keys(userFilter).length ? { $and: [{ deletedAt: null }, userFilter] } : { deletedAt: null };
    const coll = await getRawCollection<SmartTeamsCustomer>(SmartTeamsCustomer._collectionName);
    return coll.countDocuments(combinedFilter);
  },
});

export const countUsers = registerQuery({
  operationName: 'countUsers',
  type: () => Int,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: (filter?: AdornisFilter) => async (): Promise<number> => {
    const allowed = await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage));
    if (!allowed) throw new Error('Insufficient permissions to view users');
    const userFilter = filter?.getMongoFilter() ?? {};
    const combinedFilter = Object.keys(userFilter).length ? { $and: [{ deletedAt: null }, userFilter] } : { deletedAt: null };
    const coll = await getRawCollection<AdornisUser>(AdornisUser._collectionName);
    return coll.countDocuments(combinedFilter);
  },
});

// Availabilities are scoped by the current user's allowed field engineers and have no list search
// (searchFields: []), so — like the paginated subscription — the user filter is not applied.
export const countScopedAvailabilities = registerQuery({
  operationName: 'countScopedAvailabilities',
  type: () => Int,
  params: [{ name: 'filter', type: () => AdornisFilter }],
  resolve: () => async (): Promise<number> => {
    const fieldEngineerFilter = await getFieldEngineerFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(
      f => f.parseMongoFilter(),
    );
    const feCollection = await getRawCollection<SmartTeamsFieldEngineer>(SmartTeamsFieldEngineer._collectionName);
    const fieldEngineers = await feCollection.find({ ...fieldEngineerFilter, deletedAt: null }).toArray();
    const availabilityCollection = await getRawCollection<SmartTeamsAvailability>(SmartTeamsAvailability._collectionName);
    return availabilityCollection.countDocuments({
      fieldEngineerId: { $in: fieldEngineers.map(engineer => engineer._id) },
      deletedAt: null,
    });
  },
});
